import { z } from 'zod';
import { nameSchema } from './auth';

// Profile update validation
export const profileUpdateSchema = z.object({
  full_name: nameSchema,
  email: z
    .string()
    .min(1, 'Email is required')
    .email('Please enter a valid email address')
    .max(255, 'Email must be less than 255 characters'),
});

// Password change validation
export const passwordChangeSchema = z
  .object({
    currentPassword: z.string().min(1, 'Current password is required'),
    newPassword: z
      .string()
      .min(8, 'Password must be at least 8 characters')
      .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
      .regex(/[a-z]/, 'Password must contain at least one lowercase letter')
      .regex(/[0-9]/, 'Password must contain at least one number')
      .regex(/[^A-Za-z0-9]/, 'Password must contain at least one special character'),
    confirmPassword: z.string().min(1, 'Please confirm your password'),
  })
  .refine(data => data.newPassword === data.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  });

// Email change validation
export const emailChangeSchema = z
  .object({
    newEmail: z
      .string()
      .min(1, 'Email is required')
      .email('Please enter a valid email address')
      .max(255, 'Email must be less than 255 characters'),
    confirmEmail: z.string().min(1, 'Please confirm your email'),
  })
  .refine(data => data.newEmail === data.confirmEmail, {
    message: 'Emails do not match',
    path: ['confirmEmail'],
  });

// Account deletion validation
export const accountDeletionSchema = z.object({
  confirmText: z
    .string()
    .min(1, 'Please type "DELETE" to confirm')
    .refine(val => val === 'DELETE', {
      message: 'Please type "DELETE" exactly to confirm account deletion',
    }),
  password: z.string().min(1, 'Password is required to delete your account'),
});

// User preferences validation
export const userPreferencesSchema = z.object({
  theme: z.enum(['light', 'dark', 'auto']),
  notifications: z.object({
    email: z.boolean(),
    browser: z.boolean(),
    marketing: z.boolean(),
  }),
  language: z.string().min(2).max(10),
  timezone: z.string().min(1),
});

// Export types
export type ProfileUpdateFormData = z.infer<typeof profileUpdateSchema>;
export type PasswordChangeFormData = z.infer<typeof passwordChangeSchema>;
export type EmailChangeFormData = z.infer<typeof emailChangeSchema>;
export type AccountDeletionFormData = z.infer<typeof accountDeletionSchema>;
export type UserPreferencesFormData = z.infer<typeof userPreferencesSchema>;
